import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { adminUpdate } from '../../lib/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import { Settings, Save, MapPin, Webhook, Copy, ExternalLink, CheckCircle, Shield } from 'lucide-react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { LoadingSpinner } from '../../components/LoadingSpinner';

type Tab = 'business' | 'webhooks' | 'system' | 'account';

interface ConfigItem {
  key: string;
  value: string;
  description: string;
  updated_at: string;
}


export function AdminConfig() {
  const navigate = useNavigate();
  const { authUser, isAdmin, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('business');
  const [configs, setConfigs] = useState<ConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [formData, setFormData] = useState<Record<string, string>>({});

  const [businessData, setBusinessData] = useState({
    company_name: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Nederland',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    if (!authLoading) {
      if (!authUser) {
        navigate('/admin', { replace: true });
        return;
      }
      if (!isAdmin) {
        navigate('/dashboard', { replace: true });
        return;
      }
      loadData();
    }
  }, [authUser, isAdmin, authLoading, navigate]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadConfigs(), loadBusinessData()]);
    setLoading(false);
  };

  const loadConfigs = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('No session found');
        setLoading(false);
        return;
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-get-data?type=system-config`;
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to load configs:', errorText);
        setLoading(false);
        return;
      }

      const result = await response.json();
      const data = result.data || [];
      setConfigs(data);
      const initialFormData: Record<string, string> = {};
      data.forEach((config: any) => {
        initialFormData[config.key] = config.value;
      });
      setFormData(initialFormData);
    } catch (error) {
      console.error('Error loading configs:', error);
    }
  };


  const loadBusinessData = async () => {
    if (!authUser) return;

    const { data } = await supabase
      .from('users')
      .select('company_name, address, city, postal_code, country')
      .eq('auth_user_id', authUser.id)
      .maybeSingle();

    if (data) {
      setBusinessData({
        company_name: data.company_name || '',
        address: data.address || '',
        city: data.city || '',
        postal_code: data.postal_code || '',
        country: data.country || 'Nederland',
      });
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveMessage('');

    try {
      for (const config of configs) {
        const newValue = formData[config.key];
        if (newValue !== config.value) {
          await adminUpdate({
            table: 'system_config',
            operation: 'update',
            data: {
              value: newValue,
              updated_at: new Date().toISOString(),
            },
            filters: { key: config.key },
          });
        }
      }

      setSaveMessage('Configuratie succesvol opgeslagen!');
      loadConfigs();

      setTimeout(() => {
        setSaveMessage('');
      }, 3000);
    } catch (error: any) {
      setSaveMessage('Fout bij opslaan: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (key: string, value: string) => {
    setFormData({
      ...formData,
      [key]: value,
    });
  };


  const handleSaveBusinessData = async () => {
    if (!authUser) return;

    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Geen actieve sessie. Log opnieuw in.');
      }

      await adminUpdate({
        table: 'users',
        operation: 'update-by-auth-id',
        authUserId: authUser.id,
        data: businessData,
      });

      setSaveMessage('Bedrijfsgegevens opgeslagen');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      console.error('Error saving business data:', error);
      alert(`Fout bij opslaan van bedrijfsgegevens: ${error.message}`);
    }
    setSaving(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Nieuwe wachtwoorden komen niet overeen');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('Nieuw wachtwoord moet minimaal 8 karakters zijn');
      return;
    }

    if (!passwordData.currentPassword) {
      setPasswordError('Voer je huidige wachtwoord in');
      return;
    }

    setSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        throw new Error('Geen gebruiker gevonden');
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordData.currentPassword,
      });

      if (signInError) {
        throw new Error('Huidig wachtwoord is onjuist');
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });

      if (updateError) {
        throw updateError;
      }

      setPasswordSuccess('Wachtwoord succesvol gewijzigd!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      setTimeout(() => {
        setPasswordSuccess('');
      }, 5000);
    } catch (error: any) {
      console.error('Error changing password:', error);
      setPasswordError(error.message || 'Fout bij wijzigen van wachtwoord');
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center">
        <LoadingSpinner text="Configuratie laden..." />
      </div>
    );
  }

  const systemConfigs = configs.filter(c =>
    !c.key.includes('openai_api_key') &&
    !c.key.includes('leonardo_api_key') &&
    !c.key.includes('sendgrid_api_key') &&
    !c.key.includes('exchange_rate') &&
    !c.key.includes('price') &&
    !c.key.includes('cost') &&
    !c.description.includes('[DEPRECATED')
  );

  const tabs = [
    { id: 'business', label: 'Bedrijfsgegevens', icon: MapPin },
    { id: 'webhooks', label: 'Webhooks', icon: Webhook },
    { id: 'system', label: 'Systeem Configuratie', icon: Settings },
    { id: 'account', label: 'Account Beveiliging', icon: Shield },
  ];

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-[#231F20]">Configuratie</h1>
          {saveMessage && (
            <span className="text-green-600 font-medium">{saveMessage}</span>
          )}
        </div>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as Tab)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                  isActive
                    ? 'border-[#E94A47] text-[#E94A47]'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div>
          {activeTab === 'business' && (
            <div className="card">
              <h2 className="text-xl font-bold text-[#231F20] mb-4">Bedrijfsgegevens</h2>
              <p className="text-gray-600 mb-6">
                Deze gegevens worden automatisch toegevoegd aan email campaign footers (wettelijk verplicht).
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bedrijfsnaam
                  </label>
                  <input
                    type="text"
                    value={businessData.company_name}
                    onChange={(e) => setBusinessData({ ...businessData, company_name: e.target.value })}
                    placeholder="Jouw Bedrijfsnaam B.V."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#231F20] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adres
                  </label>
                  <input
                    type="text"
                    value={businessData.address}
                    onChange={(e) => setBusinessData({ ...businessData, address: e.target.value })}
                    placeholder="Straatnaam 123"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#231F20] focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Postcode
                    </label>
                    <input
                      type="text"
                      value={businessData.postal_code}
                      onChange={(e) => setBusinessData({ ...businessData, postal_code: e.target.value })}
                      placeholder="1234 AB"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#231F20] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stad
                    </label>
                    <input
                      type="text"
                      value={businessData.city}
                      onChange={(e) => setBusinessData({ ...businessData, city: e.target.value })}
                      placeholder="Amsterdam"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#231F20] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Land
                  </label>
                  <select
                    value={businessData.country}
                    onChange={(e) => setBusinessData({ ...businessData, country: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#231F20] focus:border-transparent"
                  >
                    <option value="Nederland">Nederland</option>
                    <option value="België">België</option>
                    <option value="Duitsland">Duitsland</option>
                    <option value="Frankrijk">Frankrijk</option>
                    <option value="Verenigde Staten">Verenigde Staten</option>
                    <option value="Verenigd Koninkrijk">Verenigd Koninkrijk</option>
                  </select>
                </div>
              </div>

              <button
                onClick={handleSaveBusinessData}
                disabled={saving}
                className="btn-primary mt-6 flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                Opslaan
              </button>
            </div>
          )}

          {activeTab === 'webhooks' && (
            <div className="card">
              <h2 className="text-xl font-bold text-[#231F20] mb-4">Webhook URLs</h2>
              <p className="text-gray-600 mb-6">
                Configureer deze webhook URLs in de externe services om events te ontvangen.
              </p>

              <div className="space-y-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">SendGrid Events</h3>
                      <p className="text-sm text-gray-600">Email events (delivered, opened, clicked, bounced)</p>
                    </div>
                    <a
                      href="https://app.sendgrid.com/settings/mail_settings"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      SendGrid Dashboard
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="bg-gray-50 rounded px-3 py-2 font-mono text-sm flex items-center justify-between">
                    <span className="text-gray-800 break-all">
                      https://aszkqjnlwicwymhubcda.supabase.co/functions/v1/sendgrid-webhook
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('https://aszkqjnlwicwymhubcda.supabase.co/functions/v1/sendgrid-webhook');
                        alert('URL gekopieerd!');
                      }}
                      className="ml-3 p-2 text-gray-600 hover:bg-gray-200 rounded flex-shrink-0"
                      title="Kopieer URL"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">Plug&Pay Credits</h3>
                      <p className="text-sm text-gray-600">Credit aankopen verwerken</p>
                    </div>
                    <a
                      href="https://app.plugandpay.nl"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      Plug&Pay Dashboard
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="bg-gray-50 rounded px-3 py-2 font-mono text-sm flex items-center justify-between">
                    <span className="text-gray-800 break-all">
                      https://aszkqjnlwicwymhubcda.supabase.co/functions/v1/plugandpay-webhook
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('https://aszkqjnlwicwymhubcda.supabase.co/functions/v1/plugandpay-webhook');
                        alert('URL gekopieerd!');
                      }}
                      className="ml-3 p-2 text-gray-600 hover:bg-gray-200 rounded flex-shrink-0"
                      title="Kopieer URL"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">Zapier Signup</h3>
                      <p className="text-sm text-gray-600">Nieuwe gebruikers registraties via Zapier</p>
                    </div>
                    <a
                      href="https://zapier.com/app/zaps"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      Zapier Dashboard
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="bg-gray-50 rounded px-3 py-2 font-mono text-sm flex items-center justify-between">
                    <span className="text-gray-800 break-all">
                      https://aszkqjnlwicwymhubcda.supabase.co/functions/v1/signup
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('https://aszkqjnlwicwymhubcda.supabase.co/functions/v1/signup');
                        alert('URL gekopieerd!');
                      }}
                      className="ml-3 p-2 text-gray-600 hover:bg-gray-200 rounded flex-shrink-0"
                      title="Kopieer URL"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'system' && (
            <div className="card">
              <h2 className="text-xl font-bold text-[#231F20] mb-4">Systeem Configuratie</h2>
              <p className="text-gray-600 mb-6">Beheer systeem instellingen, limieten en drempelwaarden</p>
              <form onSubmit={handleSubmit} className="space-y-6">
                {systemConfigs.map((config) => (
                  <div key={config.key}>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {config.key}
                    </label>
                    <p className="text-sm text-gray-600 mb-3">{config.description}</p>
                    <input
                      type="text"
                      value={formData[config.key] || ''}
                      onChange={(e) => handleInputChange(config.key, e.target.value)}
                      className="input w-full"
                    />
                  </div>
                ))}
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Bezig met opslaan...' : 'Opslaan'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'account' && (
            <div className="card">
              <h2 className="text-xl font-bold text-[#231F20] mb-4">Account Beveiliging</h2>
              <p className="text-gray-600 mb-6">Wijzig je wachtwoord voor veilige toegang tot het admin dashboard</p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-1">Wachtwoord Vereisten</h3>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>Minimaal 8 karakters lang</li>
                      <li>Gebruik een uniek wachtwoord dat je nergens anders gebruikt</li>
                      <li>Combineer letters, cijfers en speciale tekens voor extra beveiliging</li>
                    </ul>
                  </div>
                </div>
              </div>

              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                {passwordError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-700">{passwordError}</p>
                  </div>
                )}

                {passwordSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <p className="text-sm text-green-700 font-medium">{passwordSuccess}</p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Huidig Wachtwoord
                  </label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    placeholder="Voer je huidige wachtwoord in"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#231F20] focus:border-transparent"
                    disabled={saving}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nieuw Wachtwoord
                  </label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    placeholder="Minimaal 8 karakters"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#231F20] focus:border-transparent"
                    disabled={saving}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bevestig Nieuw Wachtwoord
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    placeholder="Voer nieuw wachtwoord nogmaals in"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#231F20] focus:border-transparent"
                    disabled={saving}
                  />
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Bezig met wijzigen...' : 'Wachtwoord Wijzigen'}
                </button>
              </form>
            </div>
          )}
      </div>
    </AdminLayout>
  );
}
